import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { apiClient, ApiError } from '@/lib/api-client';
import { env } from '@/lib/env';
import { supabaseClient } from '@/lib/supabase-client';

interface User {
  id: string;
  /** Supabase auth user id; use for Realtime notifications so RLS (auth.uid() = user_id) allows delivery */
  authUserId?: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  status?: string;
  emailVerified: boolean;
}

interface OrganizationSummary {
  id: string;
  name: string;
  slug: string;
  role: string;
  status: string;
}

interface SessionContextValue {
  user: User | null;
  organization: OrganizationSummary | null;
  organizations: OrganizationSummary[];
  token: string | null;
  isLoading: boolean;
  login: (credentials: { email: string; password: string }) => Promise<void>;
  socialLogin: (sessionToken: string) => Promise<void>;
  register: (payload: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
    organizationName: string;
  }) => Promise<{ requiresEmailVerification: boolean }>;
  logout: () => Promise<void>;
  selectOrganization: (organizationId: string) => Promise<void>;
  refresh: (overrideToken?: string | null) => Promise<boolean>;
}

const AuthContext = createContext<SessionContextValue | undefined>(undefined);

const TOKEN_KEY = 'advoltix-session-token';

/** Decode a JWT's `exp` (seconds since epoch) without verifying the signature. */
function decodeJwtExp(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: unknown };
    return typeof json.exp === 'number' ? json.exp : null;
  } catch {
    return null;
  }
}

/**
 * Return the stored session token ONLY if it is still valid (not expired, with a
 * 30s buffer). Prevents exposing a stale/expired token to consumers on reload —
 * which caused first-load 401s on authed queries (org, feature-access) before
 * Supabase silently refreshed the token. When expired/missing we start null and
 * let refresh() fetch a fresh token via getSession() before queries fire.
 */
function readValidStoredToken(): string | null {
  try {
    const t = window.localStorage.getItem(TOKEN_KEY);
    if (!t) return null;
    const exp = decodeJwtExp(t);
    // If we can't read exp, don't trust it — force a fresh fetch.
    if (exp != null && exp * 1000 > Date.now() + 30_000) return t;
    return null;
  } catch {
    return null;
  }
}

async function getSupabaseSessionToken(): Promise<string | null> {
  const { data, error } = await supabaseClient.auth.getSession();
  if (error || !data.session) return null;
  return data.session.access_token;
}

async function fetchCurrentUser(token: string | null) {
  if (!token) return null;
  try {
    const response = await apiClient.get<{ user: User }>(`/api/v1/auth/me`, token);
    return response?.user ?? null;
  } catch (error) {
    if (error instanceof ApiError && (error.status === 401 || error.status === 404)) {
      return null;
    }
    throw error;
  }
}

async function fetchOrganizations(token: string | null) {
  if (!token) return [];
  try {
    const response = await apiClient.get<{ organizations: OrganizationSummary[] }>(`/api/v1/organizations`, token);
    return response?.organizations ?? [];
  } catch (error) {
    if (error instanceof ApiError && (error.status === 401 || error.status === 404)) {
      return [];
    }
    throw error;
  }
}

async function checkOnboardingStatus(token: string | null, organizationId: string) {
  if (!token || !organizationId) return null;
  try {
    const response = await apiClient.get<{ data: any }>(
      '/api/v1/subscription-management/onboarding/progress',
      token
    );
    return response.data;
  } catch (error) {
    console.warn('Failed to check onboarding status', error);
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(() => readValidStoredToken());
  const [user, setUser] = useState<User | null>(null);
  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [organization, setOrganization] = useState<OrganizationSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const organizationRef = useRef<OrganizationSummary | null>(null);
  const refreshInProgressRef = useRef(false);
  // Refs to avoid stale closures in stable callbacks without adding token/user to deps
  const tokenRef = useRef<string | null>(token);
  const userRef = useRef<User | null>(null);

  organizationRef.current = organization;
  // Keep refs in sync with state on every render
  tokenRef.current = token;
  userRef.current = user;

  // Keep the stored token in sync with Supabase's automatic token refresh.
  // Supabase access tokens expire after 1 hour; the SDK silently issues a new one
  // via the refresh token. We update the ref and state here but do NOT call refresh()
  // again — that would create a loop (token change → refresh() → more API calls).
  useEffect(() => {
    const { data: { subscription } } = supabaseClient.auth.onAuthStateChange(
      (_event, session) => {
        if (session?.access_token) {
          const newToken = session.access_token;
          window.localStorage.setItem(TOKEN_KEY, newToken);
          tokenRef.current = newToken;
          setToken(newToken);
          // Do NOT call refresh() here. The token is updated in state/ref and will
          // be picked up automatically on the next API call. Calling refresh() here
          // would re-trigger this effect in a loop via the old [token] dep.
        } else if (_event === 'SIGNED_OUT') {
          window.localStorage.removeItem(TOKEN_KEY);
          tokenRef.current = null;
          setToken(null);
          setUser(null);
          userRef.current = null;
          setOrganizations([]);
          setOrganization(null);
        }
      }
    );
    return () => subscription.unsubscribe();
  }, []);

  const refresh = useCallback(async (overrideToken?: string | null): Promise<boolean> => {
    if (env.isE2E) {
      const mockUser: User = {
        id: 'e2e-user',
        email: 'e2e@example.com',
        firstName: 'E2E',
        lastName: 'Tester',
        emailVerified: true,
      };
      const mockOrg: OrganizationSummary = {
        id: 'e2e-org',
        name: 'E2E Org',
        slug: 'e2e-org',
        role: 'ADMIN',
        status: 'active',
      };
      setUser(mockUser);
      userRef.current = mockUser;
      setOrganizations([mockOrg]);
      setOrganization(mockOrg);
      setToken('e2e-token');
      tokenRef.current = 'e2e-token';
      setIsLoading(false);
      return true;
    }

    if (refreshInProgressRef.current && overrideToken == null) {
      // Use ref so we don't read a stale closure value
      return userRef.current != null;
    }
    refreshInProgressRef.current = true;

    // Use ref to read current token — this is why token is NOT in useCallback deps.
    // The ref is always current because we set tokenRef.current on every render and
    // in the onAuthStateChange listener.
    let activeToken = overrideToken ?? tokenRef.current;
    setIsLoading(true);

    try {
      // Unless the caller passed an explicit token (login flow), always resolve the
      // freshest token from Supabase first. getSession() silently refreshes an
      // expired access token, so the initial fetchCurrentUser/fetchOrganizations
      // never go out with a stale token (which previously caused first-load 401s).
      if (overrideToken == null) {
        const { data } = await supabaseClient.auth.getSession();
        const fresh = data.session?.access_token ?? null;
        if (fresh) {
          activeToken = fresh;
          if (fresh !== tokenRef.current) {
            window.localStorage.setItem(TOKEN_KEY, fresh);
            tokenRef.current = fresh;
            setToken(fresh);
          }
        }
      }

      let [userResponse, orgs] = await Promise.all([
        fetchCurrentUser(activeToken),
        fetchOrganizations(activeToken),
      ]);

      // If the backend rejected our token (401 → userResponse is null), try one
      // silent Supabase token refresh before giving up. This handles the window where
      // the access token has just expired but onAuthStateChange hasn't fired yet.
      if (!userResponse && activeToken) {
        const { data: refreshData } = await supabaseClient.auth.refreshSession();
        if (refreshData.session?.access_token) {
          const freshToken = refreshData.session.access_token;
          window.localStorage.setItem(TOKEN_KEY, freshToken);
          tokenRef.current = freshToken;
          setToken(freshToken);
          activeToken = freshToken;
          [userResponse, orgs] = await Promise.all([
            fetchCurrentUser(freshToken),
            fetchOrganizations(freshToken),
          ]);
        }
      }

      setUser(userResponse);
      userRef.current = userResponse;
      setOrganizations(orgs);

      // Still no valid user after refresh attempt → session is genuinely invalid
      if (!userResponse) {
        window.localStorage.removeItem(TOKEN_KEY);
        tokenRef.current = null;
        setToken(null);
        return false;
      }

      if (!activeToken || orgs.length === 0) {
        setOrganization(null);
        if (overrideToken && userResponse) {
          const currentPath = window.location.pathname;
          if (currentPath === '/auth/login' || currentPath === '/auth/register') {
            if (!userResponse.emailVerified) {
              navigate('/auth/verify-email', { replace: true });
            } else {
              navigate('/app/dashboard', { replace: true });
            }
          }
        }
        return true;
      }

      const selectedOrg = (() => {
        const current = organizationRef.current;
        if (current && orgs.some(org => org.id === current.id)) {
          return orgs.find(org => org.id === current.id) ?? orgs[0];
        }
        return orgs[0];
      })();

      setOrganization(selectedOrg);

      if (userResponse && !userResponse.emailVerified) {
        window.localStorage.removeItem(TOKEN_KEY);
        tokenRef.current = null;
        setToken(null);
        setUser(null);
        userRef.current = null;
        setOrganizations([]);
        setOrganization(null);
        setIsLoading(false);
        navigate('/auth/verify-email', { replace: true });
        return false;
      }

      if (selectedOrg && activeToken) {
        try {
          const onboardingStatus = await checkOnboardingStatus(activeToken, selectedOrg.id);
          if (onboardingStatus && !onboardingStatus.organization?.onboardingCompleted) {
            navigate('/onboarding');
            return true;
          }
        } catch (error) {
          console.warn('Failed to check onboarding status', error);
        }
      }

      if (overrideToken && userResponse && userResponse.emailVerified) {
        const currentPath = window.location.pathname;
        if (currentPath === '/auth/login' || currentPath === '/auth/register') {
          navigate('/app/dashboard', { replace: true });
        }
      }
      return true;
    } catch (error) {
      // Only clear the session for explicit auth rejections (401).
      // For transient failures (network down, 5xx, timeout) keep the current
      // session alive so the user isn't logged out due to a momentary server blip.
      if (error instanceof ApiError && error.status === 401) {
        console.warn('[auth] Token rejected by server (401) — clearing session');
        window.localStorage.removeItem(TOKEN_KEY);
        tokenRef.current = null;
        setToken(null);
        setUser(null);
        userRef.current = null;
        setOrganizations([]);
        setOrganization(null);
        return false;
      }
      console.warn('[auth] Session refresh failed transiently — keeping current session', error);
      // Return true if we already had a user (keep them logged in)
      return userRef.current != null;
    } finally {
      setIsLoading(false);
      refreshInProgressRef.current = false;
    }
    // navigate is stable (from react-router). token is intentionally excluded —
    // we read it via tokenRef.current to keep this callback stable and avoid the
    // token-change → refresh() → token-change loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Run once on mount. refresh() is now stable (no token dep) so this won't
  // re-fire on every Supabase token rotation like it did before.
  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(
    async ({ email, password }: { email: string; password: string }) => {
      if (env.isE2E) {
        window.localStorage.setItem(TOKEN_KEY, 'e2e-token');
        tokenRef.current = 'e2e-token';
        setToken('e2e-token');
        await refresh('e2e-token');
        return;
      }

      const { error } = await supabaseClient.auth.signInWithPassword({ email, password });
      if (error) {
        const lower = error.message.toLowerCase();
        const looksUnverified =
          lower.includes('email') && (lower.includes('confirm') || lower.includes('verified'));
        if (looksUnverified) {
          navigate('/auth/verify-email', { replace: true });
          return;
        }
        throw new Error(error.message);
      }

      const supaToken = await getSupabaseSessionToken();
      if (!supaToken) {
        throw new Error('Login succeeded in Supabase but no access token was returned.');
      }

      window.localStorage.setItem(TOKEN_KEY, supaToken);
      tokenRef.current = supaToken;
      setToken(supaToken);
      await refresh(supaToken);

      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'login', { method: 'email' });
      }
    },
    [refresh, navigate]
  );

  const register = useCallback(
    async ({
      email,
      password,
      firstName,
      lastName,
      organizationName,
    }: {
      email: string;
      password: string;
      firstName?: string;
      lastName?: string;
      organizationName: string;
    }): Promise<{ requiresEmailVerification: boolean }> => {
      if (env.isE2E) {
        window.localStorage.setItem(TOKEN_KEY, 'e2e-token');
        tokenRef.current = 'e2e-token';
        setToken('e2e-token');
        await refresh('e2e-token');
        return { requiresEmailVerification: false };
      }

      // Registration goes through our own backend (not supabaseClient.auth.signUp
      // directly) because it's the only path that also creates the organization,
      // trial window, and membership row — signUp alone leaves the user orgless.
      await apiClient.post('/api/v1/auth/register', {
        email,
        password,
        firstName,
        lastName,
        organizationName,
      });

      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'sign_up', { method: 'email' });
      }

      return { requiresEmailVerification: true };
    },
    [refresh]
  );

  const socialLogin = useCallback(
    async (sessionToken: string) => {
      window.localStorage.setItem(TOKEN_KEY, sessionToken);
      tokenRef.current = sessionToken;
      setToken(sessionToken);
      const sessionLoaded = await refresh(sessionToken);
      if (!sessionLoaded) {
        window.localStorage.removeItem(TOKEN_KEY);
        tokenRef.current = null;
        setToken(null);
        throw new Error(
          'Session could not be loaded. The sign-in link may have expired or the backend could not verify your account. Please try again or sign in with email.'
        );
      }
    },
    [refresh]
  );

  const logout = useCallback(async () => {
    if (env.isE2E) {
      window.localStorage.removeItem(TOKEN_KEY);
      tokenRef.current = null;
      setToken(null);
      setUser(null);
      userRef.current = null;
      setOrganizations([]);
      setOrganization(null);
      setIsLoading(false);
      return;
    }

    window.localStorage.removeItem(TOKEN_KEY);
    tokenRef.current = null;
    setToken(null);
    setUser(null);
    userRef.current = null;
    setOrganizations([]);
    setOrganization(null);
    setIsLoading(false);

    try {
      await supabaseClient.auth.signOut();
    } catch (error) {
      console.warn('Supabase signOut failed, but client logout completed', error);
    }

    navigate('/auth/login', { replace: true });
  }, [navigate]);

  const selectOrganization = useCallback(async (organizationId: string) => {
    if (!tokenRef.current) return;
    setOrganization(prev => {
      if (prev?.id === organizationId) return prev;
      const match = organizations.find(org => org.id === organizationId);
      return match ?? prev;
    });
  }, [organizations]);

  const value = useMemo<SessionContextValue>(
    () => ({
      user,
      organization,
      organizations,
      token,
      isLoading,
      login,
      socialLogin,
      register,
      logout,
      selectOrganization,
      refresh,
    }),
    [user, organization, organizations, token, isLoading, login, socialLogin, register, logout, selectOrganization, refresh]
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).__AUTH_PROVIDER_STATE__ = { user, isLoading };
    }
  }, [user, isLoading]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).__AUTH_PROVIDER_STATE__ = { user, isLoading };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

/** Use when component may render outside AuthProvider (e.g. error boundaries). Returns undefined if not within provider. */
export function useAuthOptional(): SessionContextValue | undefined {
  return useContext(AuthContext);
}
